import {
    deleteProperty,
    getPhysicalItemTextAlignFromLogical,
    migratePropertyWithinStyleItem,
    parseValueWithUnit,
    setProperty
} from './utils'
import type {ComponentProps} from './types'
import type {SerializedCompStructure, StyleRef} from '@wix/document-services-types'
import {MENU_DROPDOWN_ICON} from './constants'

const alignmentMapper = {
    'flex-start': 'start',
    'flex-end': 'end',
    center: 'center',
    justify: 'justify'
}

const migrateDropdownIcon = (styleItem: StyleRef) => {
    setProperty({
        key: 'spacing-between-label-and-dropdown-icon',
        value: MENU_DROPDOWN_ICON.SPACING_BETWEEN_LABEL_AND_DROPDOWN_ICON,
        styleItem
    })
    setProperty({
        key: 'item-icon-size',
        value: MENU_DROPDOWN_ICON.ICON_SIZE,
        styleItem
    })
    setProperty({
        key: 'item-icon-color',
        value: styleItem.style.properties['item-color'],
        styleItem
    })
    setProperty({
        key: 'item-hover-icon-color',
        value: styleItem.style.properties['item-hover-color'],
        styleItem
    })
    setProperty({
        key: 'item-selected-icon-color',
        value: styleItem.style.properties['item-selected-color'],
        styleItem
    })
}

const migrateOverflow = (styleItem: StyleRef, componentProps: ComponentProps) => {
    setProperty({
        key: 'overflow',
        value: componentProps.menuMode === 'wrap' ? 'wrap' : 'scroll',
        styleItem
    })
}

const migratePadding = (styleItem: StyleRef) => {
    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'item-padding-top',
        toKey: 'item-vertical-padding',
        fallback: '0px'
    })

    migratePropertyWithinStyleItem({
        styleItem,
        fromKey: 'item-padding-right',
        toKey: 'item-horizontal-padding',
        fallback: '0px'
    })
}

const migrateSpacing = (styleItem: StyleRef) => {
    // in Stylable Horizontal Menu the spacing between items is the same as the margin between items
    // in Stylable Horizontal Menu layout panel, when you set spacing=x, it sets margin-left and margin-right to x/2
    const getSpacing = (marginProperty: string) => {
        if (!styleItem.style.properties[marginProperty]) {
            return
        }

        const {value, unit} = parseValueWithUnit(styleItem.style.properties[marginProperty])
        return `${value * 2}${unit}`
    }

    setProperty({
        key: 'menu-items-main-axis-gap',
        value: getSpacing('item-margin-left'),
        styleItem
    })

    setProperty({
        key: 'menu-items-cross-axis-gap',
        value: getSpacing('item-margin-top'),
        styleItem
    })

    deleteProperty({
        styleItem,
        key: 'item-margin-left'
    })

    deleteProperty({
        styleItem,
        key: 'item-margin-top'
    })
}

const migrateJustification = (styleItem: StyleRef) => {
    const menuFlexGrow = styleItem.style.properties['menu-flex-grow'] ?? '0'
    const isFlexGrowOne = menuFlexGrow === '1'

    if (isFlexGrowOne) {
        setProperty({
            key: 'menu-items-justification',
            value: alignmentMapper.justify,
            styleItem
        })

        setProperty({
            key: 'container-align',
            value: alignmentMapper.justify,
            styleItem
        })
    } else {
        setProperty({
            key: 'menu-items-justification',
            value: 'none',
            styleItem
        })

        setProperty({
            key: 'container-align',
            value: alignmentMapper[styleItem.style.properties['menu-justify-content'] ?? 'center'],
            styleItem
        })
    }
}

const migrateTextAlignment = (styleItem: StyleRef) => {
    setProperty({
        key: 'item-text-align',
        value: getPhysicalItemTextAlignFromLogical(styleItem),
        styleItem
    })
}

const castToInheritIfNeeded = (direction: 'ltr' | 'rtl' | undefined, parentDirection: 'ltr' | 'rtl') => {
    return direction === parentDirection || !direction ? 'inherit' : direction
}

const migrateMenuDirections = (data: SerializedCompStructure['data'], styleItem: StyleRef) => {
    // if this migration will be ever run as "seamless migration"
    // we should find a way to pass site direction as an argument
    const siteDirection = 'ltr'

    const menuDirection = styleItem.style.properties['menu-direction']
    const itemDirection = styleItem.style.properties['item-direction']
    const submenuItemDirection = styleItem.style.properties['dropdown-menu-item-direction']
    const submenuSubItemDirection = styleItem.style.properties['dropdown-menu-sub-item-direction']

    data.direction = castToInheritIfNeeded(menuDirection, siteDirection)
    data.itemDirection = castToInheritIfNeeded(itemDirection, menuDirection)
    data.submenuDirection = castToInheritIfNeeded(submenuItemDirection, menuDirection)
    data.submenuItemDirection = castToInheritIfNeeded(submenuItemDirection, menuDirection)
    data.submenuSubItemDirection = castToInheritIfNeeded(submenuSubItemDirection, menuDirection)
}

const migrateMenuLayout = (styleItem: StyleRef, componentProps: ComponentProps) => {
    migrateOverflow(styleItem, componentProps)
    migratePadding(styleItem)
    migrateSpacing(styleItem)
    migrateJustification(styleItem)
    migrateTextAlignment(styleItem)
    migrateDropdownIcon(styleItem)
}

export {migrateMenuLayout, migrateMenuDirections}
